<script setup lang="ts">
const { t, tm, rt } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()
const localePath = useLocalePath()

useReveal()

const siteUrl = config.public.siteUrl as string
const canonical = computed(() => {
  const path = route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

useHead(() => ({
  title: t('about.meta.title'),
  meta: [
    { name: 'description',        content: t('about.meta.description') },
    { property: 'og:title',       content: t('about.meta.title') },
    { property: 'og:description', content: t('about.meta.description') },
    { property: 'og:url',         content: canonical.value },
    { name: 'twitter:title',       content: t('about.meta.title') },
    { name: 'twitter:description', content: t('about.meta.description') },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
}))

type ValueItem = { icon: string; title: string; text: string }
type Contact   = { labelKey: string; value: string; kind: 'email' | 'telegram' }

const values = computed(() =>
  (tm('about.values.items') as any[]).map((it) => ({
    icon: rt(it.icon), title: rt(it.title), text: rt(it.text),
  })) as ValueItem[],
)

// Contact endpoints are hardcoded — they aren't translated, and putting an
// "@" character into a vue-i18n message string makes the message compiler
// try to parse it as a @:link reference. Hardcode the addresses here and
// only translate the human-readable labels.
const contacts: Contact[] = [
  { labelKey: 'about.contact.labels.general',      value: 'hello@romba.am',    kind: 'email' },
  { labelKey: 'about.contact.labels.partnerships', value: 'partners@romba.am', kind: 'email' },
  { labelKey: 'about.contact.labels.press',        value: 'press@romba.am',    kind: 'email' },
  { labelKey: 'about.contact.labels.support',      value: 'support@romba.am',  kind: 'email' },
  { labelKey: 'about.contact.labels.telegram',     value: 'rodstvennik_team',         kind: 'telegram' },
]

function hrefFor(c: Contact): string {
  if (c.kind === 'email') return `mailto:${c.value}`
  if (c.kind === 'telegram') return `https://t.me/${c.value}`
  return '#'
}
function iconFor(c: Contact): string {
  return c.kind === 'telegram' ? '✈' : '✉︎'
}
function displayFor(c: Contact): string {
  return c.kind === 'telegram' ? `@${c.value}` : c.value
}
</script>

<template>
  <div>
    <SiteNav />

    <main>
      <PageHero
        :eyebrow="t('about.hero.eyebrow')"
        :title="t('about.hero.title')"
        :lead="t('about.hero.lead')"
      />

      <!-- Mission -->
      <section class="rod-section">
        <div class="rod-container max-w-3xl">
          <h2 class="rod-h2 mb-8" data-reveal>{{ t('about.mission.title') }}</h2>
          <div class="flex flex-col gap-5 text-[var(--color-text-muted)] leading-relaxed text-lg" data-reveal>
            <p>{{ t('about.mission.p1') }}</p>
            <p>{{ t('about.mission.p2') }}</p>
            <p>{{ t('about.mission.p3') }}</p>
          </div>
        </div>
      </section>

      <!-- Values -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">🧭 {{ t('about.values.eyebrow') }}</div>
            <h2 class="rod-h2">{{ t('about.values.title') }}</h2>
          </div>
          <div class="grid md:grid-cols-3 gap-5">
            <div
              v-for="(v, i) in values"
              :key="i"
              class="rod-card flex flex-col gap-4"
              data-reveal
            >
              <div class="grid place-items-center w-12 h-12 rounded-2xl"
                   style="background: linear-gradient(140deg, rgba(245,158,11,0.18) 0%, rgba(16,185,129,0.10) 100%);
                          border: 1px solid rgba(245,158,11,0.25);
                          font-size: 22px;">
                {{ v.icon }}
              </div>
              <h3 class="rod-h3 leading-tight" style="font-size: 1.1rem;">{{ v.title }}</h3>
              <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ v.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact -->
      <section id="contact" class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5">📬 {{ t('about.contact.eyebrow') }}</div>
            <h2 class="rod-h2 mb-4">{{ t('about.contact.title') }}</h2>
            <p class="text-lg text-[var(--color-text-muted)] leading-relaxed">{{ t('about.contact.lead') }}</p>
          </div>
          <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <a
              v-for="(c, i) in contacts"
              :key="i"
              :href="hrefFor(c)"
              :target="c.kind === 'telegram' ? '_blank' : undefined"
              :rel="c.kind === 'telegram' ? 'noopener noreferrer' : undefined"
              class="rod-card flex items-start gap-3 group"
              data-reveal
            >
              <div class="grid place-items-center w-10 h-10 rounded-xl flex-shrink-0"
                   :style="c.kind === 'telegram'
                     ? 'background: rgba(20,197,219,0.14); color: #6FE3F1; font-size: 18px;'
                     : 'background: rgba(245,158,11,0.14); color: var(--color-gold-300); font-size: 18px;'">
                {{ iconFor(c) }}
              </div>
              <div>
                <div class="text-xs uppercase tracking-wider text-[var(--color-text-dim)]">{{ t(c.labelKey) }}</div>
                <div class="font-mono text-sm group-hover:text-white transition-colors">{{ displayFor(c) }}</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="rod-card rod-card--glow text-center py-14 px-6 lg:px-12 max-w-4xl mx-auto" data-reveal
               style="background: linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(16,185,129,0.08) 100%); border-color: rgba(245,158,11,0.30);">
            <h2 class="rod-h2 mb-5 rod-gradient-text">{{ t('about.cta.title') }}</h2>
            <p class="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-10">{{ t('about.cta.lead') }}</p>
            <div class="flex flex-wrap justify-center gap-4">
              <a :href="`${localePath('/')}#cta`" class="rod-btn-primary">{{ t('about.cta.primary') }}</a>
              <a :href="localePath('/couriers')" class="rod-btn-secondary">{{ t('about.cta.secondary') }}</a>
              <a :href="localePath('/partners')" class="rod-btn-secondary">{{ t('about.cta.tertiary') }}</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>
